import { useState, useEffect } from 'react';
import loadImage from 'blueimp-load-image/js';
import LoadingGif from '../../table/table-loader/assets/table-loader.gif';
import { uploadFileToS3 } from '@lua-frontend/utils';
import { Images } from '../../assets/index';
import { useImageCrop } from './useImageCrop';

/**
 * Custom hook that contains all the upload logic
 */
export const useUploadImage = ({
  field,
  form,
  enableCrop = false,
  folderPath,
  prefix,
  isPrivate = false,
  apiUrl,
  cropVariant = 'square', // new: basic variant selection
  cropPresets, // optional: custom variant map
  skiptemp = false, // new: skip temp bucket for direct upload
  useAcceleratedEndpoint = false
}) => {
  const {
    logoModal,
    setLogoModal,
    upImg,
    setUpImg,
    crop,
    setCrop,
    base64ImgRef,
    onLoad,
    makeClientCrop,
    getCroppedFile,
    setImageType
  } = useImageCrop();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isImageLoaded, setIsImageLoaded] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (!field?.value) {
      setImagePreview(null);
    }
  }, [field?.value]);

  const getVariantAspect = () => {
    const variants = {
      square: 1,
      landscape: 16 / 9,
      portrait: 3 / 4,
      free: undefined,
      ...(cropPresets || {})
    };
    return variants[cropVariant];
  };

  /**
   * Helper to upload a file directly to S3 (used for non-image files or when cropping fails)
   */
  const uploadFileDirectly = async (file) => {
    setIsUploading(true);
    setUploadError(null);

    try {
      let apiBaseUrl = import.meta.env.VITE_APP_NODE_API_ENDPOINT;

      const options = {
        folderPath: folderPath,
        prefix: prefix,
        isPrivate: isPrivate,
        apiUrl: apiUrl || apiBaseUrl || undefined,
        skipTempBucket: skiptemp,
        useAcceleratedEndpoint: useAcceleratedEndpoint,
        onStart: () => {
          setIsUploading(true);
          if (form && form.setFieldValue) form.setFieldValue('error', '');
        },
        onComplete: () => {
          setIsUploading(false);
        },
        onError: (error) => {
          setUploadError(error);
          setIsUploading(false);
          if (form && form.setFieldValue)
            form.setFieldValue('error', error?.message || 'Upload failed');
        }
      };

      const result = await uploadFileToS3(file, options);

      if (result) {
        // Set field value to the S3 key
        form.setFieldValue(field.name, result.key);
        form.setFieldValue(`${field.name}Changed`, true);

        if (form && form.setFieldValue) form.setFieldValue('error', '');
        // Create preview URL for display
        setImagePreview(URL.createObjectURL(file));
      } else {
        const error = new Error('Upload failed - no result returned');
        setUploadError(error);
        if (form && form.setFieldValue) form.setFieldValue('error', error.message);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown upload error');
      setUploadError(error);
      setIsUploading(false);
      if (form && form.setFieldValue) form.setFieldValue('error', error.message);
    }
  };

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      const fileType = selectedFile.type || '';

      // For direct upload mode, upload immediately to S3 (temp bucket)
      if (enableCrop === false) {
        uploadFileDirectly(selectedFile);
        return;
      }

      // For crop mode, prepare the image for cropping
      if (enableCrop) {
        // Clear any previous upload error in the parent form
        if (form && form.setFieldValue) {
          form.setFieldValue('error', '');
        }

        const aspect = getVariantAspect();
        setCrop({
          ...crop,
          unit: '%',
          width: 200,
          height: 'unset',
          ...(typeof aspect === 'number' ? { aspect } : {})
        });

        loadImage(
          selectedFile,
          (img) => {
            // loadImage returns a canvas when canvas option is true
            if (typeof img.toDataURL !== 'function') {
              // Fallback – something went wrong, abort cropping and upload the raw image as-is
              uploadFileDirectly(selectedFile);
              return;
            }
            const base64data = img.toDataURL(fileType);
            setUpImg(base64data);
            setLogoModal(true);
            setImageType(fileType);
          },
          { orientation: true, canvas: true }
        );
      }
    }
  };

  const resetImage = (event) => {
    event.preventDefault();
    form.setFieldValue(field.name, '');
    form.setFieldValue(`${field.name}Changed`, true);
    setUpImg('');
    setImagePreview(null);
  };

  const handleImageLoaded = () => {
    setIsImageLoaded(false);
  };

  const handleImageErrored = (ev) => {
    ev.target.src = 'https://d1uj83pvrphe42.cloudfront.net/default/default_profile_pic.png';
    setIsImageLoaded(false);
  };

  // Determine the image source
  const imageSource = imagePreview
    ? imagePreview
    : field?.value
      ? field.value
      : 'https://d1uj83pvrphe42.cloudfront.net/default/default_profile_pic.png';

  const onCropComplete = async () => {
    try {
      if (!base64ImgRef.current) {
        return;
      }

      const file = await getCroppedFile();
      if (!file) {
        console.error('No cropped file available');
        return;
      }

      setImagePreview(URL.createObjectURL(file));
      setIsUploading(true);
      setUploadError(null);

      try {
        let apiBaseUrl = import.meta.env.VITE_APP_NODE_API_ENDPOINT;

        const options = {
          folderPath: folderPath,
          prefix: prefix,
          isPrivate: isPrivate,
          apiUrl: apiUrl || apiBaseUrl || undefined,
          skipTempBucket: skiptemp,
          useAcceleratedEndpoint: useAcceleratedEndpoint,
          onStart: () => {
            setIsUploading(true);
            if (form && form.setFieldValue) form.setFieldValue('error', '');
          },
          onComplete: () => {
            setIsUploading(false);
          },
          onError: (error) => {
            setUploadError(error);
            setIsUploading(false);
            if (form && form.setFieldValue)
              form.setFieldValue('error', error?.message || 'Upload failed');
          }
        };

        const result = await uploadFileToS3(file, options);

        if (result) {
          // Update the Formik field value with the file key
          form.setFieldValue(field.name, result.key);
          form.setFieldValue(`${field.name}Changed`, true);

          if (form && form.setFieldValue) form.setFieldValue('error', '');
          // Close the modal after successful upload
          setLogoModal(false);
        } else {
          setUploadError(new Error('Upload failed - no result returned'));
          if (form && form.setFieldValue)
            form.setFieldValue('error', 'Upload failed - no result returned');
        }
      } catch (uploadErr) {
        setUploadError(uploadErr instanceof Error ? uploadErr : new Error('Unknown upload error'));
        setIsUploading(false);
        if (form && form.setFieldValue)
          form.setFieldValue('error', uploadErr?.message || 'Unknown upload error');
      }
    } catch (outerErr) {
      setUploadError(outerErr instanceof Error ? outerErr : new Error('Critical error occurred'));
      setIsUploading(false);
      if (form && form.setFieldValue)
        form.setFieldValue('error', outerErr?.message || 'Critical error occurred');
    }
  };

  // Minimized, headless return
  return {
    // Common state
    isUploading,
    uploadError,
    isImageLoaded,
    imagePreview,
    imageSource,
    // Common handlers
    onSelectFile,
    resetImage,
    handleImageLoaded,
    handleImageErrored,
    // Common UI deps
    LoadingGif,
    Images,
    // Crop API only when enabled
    ...(enableCrop
      ? {
          crop: {
            isOpen: logoModal,
            src: upImg,
            rect: crop,
            open: () => setLogoModal(true),
            close: () => setLogoModal(false),
            setRect: setCrop,
            onImageLoaded: onLoad,
            onChange: setCrop,
            makePreview: makeClientCrop,
            confirm: onCropComplete,
            variant: cropVariant
          }
        }
      : {})
  };
};
